import { registerPlugin } from '@capacitor/core'
import { callAudioRouteArbiter, type HardwareAudioRoute } from '../../utils/call-audio-route-arbiter'
import { isNativeApp } from '../../utils/platform'

/**
 * In-app Capacitor plugin (frontend/native/android/AudioRoutePlugin.java +
 * AudioRoutePlugin.swift). The WebView has no setSinkId on native — speaker /
 * earpiece / Bluetooth / car routing must go through the platform.
 */
export type CallAudioOutputKind = HardwareAudioRoute

export type CallAudioOutputDevice = {
  id: string
  name: string
  kind: CallAudioOutputKind
}

export type CallAudioRouteSnapshot = {
  kind: CallAudioOutputKind
  name: string
  availableExternal: boolean
  devices: CallAudioOutputDevice[]
}

type AudioRoutePluginApi = {
  isSupported(): Promise<{ supported: boolean }>
  setSpeakerphone(options: { on: boolean; forceBuiltin?: boolean }): Promise<{ on: boolean }>
  prepareForRinging(options?: { on?: boolean }): Promise<void>
  reset(): Promise<void>
  openAppSettings(): Promise<void>
  checkMicrophonePermission(): Promise<{ state: 'granted' | 'denied' | 'prompt' }>
  /** Android 136+: shows the OS dialog; resolves `denied` at once when Android won't show it. */
  requestMicrophonePermission?(): Promise<{ state: 'granted' | 'denied' | 'prompt' }>
  getRouteSnapshot?(): Promise<CallAudioRouteSnapshot>
  setOutputDevice?(options: { id: string }): Promise<{ ok: boolean }>
  showRoutePicker?(): Promise<{ shown: boolean }>
  addListener?(
    eventName: 'routeChanged',
    listener: (data: CallAudioRouteSnapshot) => void,
  ): Promise<{ remove: () => Promise<void> }>
}

const AudioRoute = registerPlugin<AudioRoutePluginApi>('AudioRoute')

function userForcedBuiltin(): boolean {
  const output = callAudioRouteArbiter.snapshot().userOutput
  return output === 'earpiece' || output === 'speaker'
}

/**
 * Last hardware route snapshot JS has seen (native `routeChanged` events,
 * explicit snapshot fetches, and Telecom/CallKit `callAudioRoute` events).
 * Lets the JS layer honor external precedence WITHOUT a plugin round trip.
 */
let lastRouteSnapshot: CallAudioRouteSnapshot | null = null
const routeSnapshotListeners = new Set<(snapshot: CallAudioRouteSnapshot) => void>()
let nativeRouteListenerStarted = false

function rememberSnapshot(snapshot: CallAudioRouteSnapshot): void {
  lastRouteSnapshot = snapshot
  for (const listener of routeSnapshotListeners) {
    try {
      listener(snapshot)
    } catch {
      /* listener errors never break routing */
    }
  }
}

function ensureNativeRouteListener(): void {
  if (nativeRouteListenerStarted || !isNativeApp()) return
  if (typeof AudioRoute.addListener !== 'function') return
  nativeRouteListenerStarted = true
  void AudioRoute.addListener('routeChanged', (snapshot) => {
    if (snapshot && typeof snapshot.kind === 'string') rememberSnapshot(snapshot)
  }).catch(() => {
    nativeRouteListenerStarted = false
  })
}

/**
 * Event-driven route snapshot feed (Android emits `routeChanged` from the
 * plugin; iOS is kept fresh by `noteHardwareRoute` + explicit fetches).
 * Replaces the chip's 2s polling. Returns an unsubscribe.
 */
export function subscribeNativeRouteSnapshot(
  listener: (snapshot: CallAudioRouteSnapshot) => void,
): () => void {
  routeSnapshotListeners.add(listener)
  ensureNativeRouteListener()
  return () => {
    routeSnapshotListeners.delete(listener)
  }
}

export function getCachedRouteSnapshot(): CallAudioRouteSnapshot | null {
  return lastRouteSnapshot
}

/**
 * Feed a Telecom/CallKit `callAudioRoute` event into the cache (iOS has no
 * plugin-level routeChanged event). Only updates the coarse kind — device
 * lists stay whatever the last full snapshot reported.
 */
export function noteHardwareRoute(kind: HardwareAudioRoute): void {
  const previous = lastRouteSnapshot
  const external = kind === 'bluetooth' || kind === 'wired' || kind === 'car'
  rememberSnapshot({
    kind,
    name: previous && previous.kind === kind ? previous.name : '',
    // A builtin event clears the flag (a stale sticky true would block JS
    // route applies forever after the device disconnects); Android's full
    // routeChanged snapshots carry the authoritative value.
    availableExternal: external,
    devices: previous?.devices ?? [],
  })
}

/**
 * True while a car / Bluetooth / wired device ACTUALLY carries the call
 * audio. While this holds and the user has not explicitly forced a builtin,
 * JS must issue NO native route commands — every setCommunicationDevice /
 * override renegotiates the SCO link and bounced the head-unit Bluetooth
 * connection in build 104.
 *
 * Availability alone is NOT in effect (2026-10-19): with the car available
 * but the call flipped to the earpiece, staying hands-off left the driver
 * without audio. The native plugin then asks Telecom for Bluetooth back.
 */
export function externalRouteInEffect(): boolean {
  const snapshot = lastRouteSnapshot
  if (!snapshot) return false
  return snapshot.kind === 'bluetooth' || snapshot.kind === 'wired' || snapshot.kind === 'car'
}

/** External device connected but the call is on a builtin (the flip native must undo). */
export function externalAvailableButBuiltin(): boolean {
  const snapshot = lastRouteSnapshot
  if (!snapshot) return false
  return snapshot.availableExternal === true && !externalRouteInEffect()
}

/** Returns true when the route change was applied natively (or intentionally skipped). */
export async function setNativeSpeakerphone(on: boolean): Promise<boolean> {
  if (!isNativeApp()) return false
  // External precedence: the desired route IS the external device; report
  // handled so callers don't fall back to WebRTC output juggling. The native
  // plugins enforce the same invariant — skipping here avoids the round trip
  // and keeps older installed shells from bouncing SCO.
  if (!userForcedBuiltin() && externalRouteInEffect()) return true
  try {
    await AudioRoute.setSpeakerphone({ on, forceBuiltin: userForcedBuiltin() })
    return true
  } catch {
    // Plugin missing (old APK) or route unavailable — caller falls back.
    return false
  }
}

/**
 * Prepare the native audio session for the ringing phase and route the ring to
 * the correct output.
 *
 * `speakerOn` mirrors the call's isSpeakerOn/defaultSpeakerOn:
 *  - false (VOICE outgoing ringback): route the dial tone to the EARPIECE so it
 *    is held to the ear, NOT blasted through the loud speaker (the reported
 *    ringback bug). iOS uses .playAndRecord/.voiceChat→receiver; Android uses
 *    MODE_IN_COMMUNICATION→earpiece. Android Auto / car / BT: native leaves
 *    the Telecom route alone so ringback can follow the projected output.
 *  - true (INCOMING ringtone, or a VIDEO call's ringback): keep the MEDIA path so
 *    the ring plays at the media/ringer volume through the loud speaker (you want
 *    to hear an incoming ring across the room), avoiding the boosted in-call
 *    voice channel that made the ring "blast" at call volume.
 *
 * The live arbiter always wins over a stale caller flag. Incoming-ringtone is
 * the only phase that defaults to speaker; outgoing-ringback / connecting /
 * connected native voice stay on the ear (unless the user toggled).
 */
export async function prepareNativeRingback(speakerOn = false): Promise<boolean> {
  if (!isNativeApp()) return false
  callAudioRouteArbiter.setNative(true)
  // A live arbiter wins over a stale ringtone `speakerOn` so an incoming-ring
  // prepare cannot clobber connecting/connected earpiece (the overlapping
  // prepareForRinging:speaker + setSpeakerphone:off fight in telemetry).
  const on = callAudioRouteArbiter.isActive()
    ? callAudioRouteArbiter.desiredSpeakerOn()
    : speakerOn
  try {
    await AudioRoute.prepareForRinging({ on })
    return true
  } catch {
    return false
  }
}

/** Pin the native route to the arbiter's current target (user toggle or default). */
export async function applyDesiredNativeSpeakerphone(): Promise<boolean> {
  return setNativeSpeakerphone(callAudioRouteArbiter.desiredSpeakerOn())
}

/** Restore normal audio mode after a call ends. Best-effort. */
export function resetNativeAudioRoute(): void {
  callAudioRouteArbiter.reset()
  if (!isNativeApp()) return
  void AudioRoute.reset().catch(() => {
    /* plugin missing — nothing to reset */
  })
}

/**
 * Non-prompting native microphone permission probe (AVCaptureDevice on iOS,
 * checkSelfPermission on Android). The WebView itself cannot READ mic
 * permission — getUserMedia prompts and navigator.permissions is unsupported
 * in WKWebView — which is why the walkthrough used to mis-report the mic as
 * un-granted on every replay. Returns 'prompt' when the plugin method is
 * missing (old installed shell) so callers keep the old behavior.
 */
export async function checkNativeMicrophonePermission(): Promise<'granted' | 'denied' | 'prompt'> {
  if (!isNativeApp()) return 'prompt'
  try {
    const { state } = await AudioRoute.checkMicrophonePermission()
    return state
  } catch {
    return 'prompt'
  }
}

/**
 * Android: ask for the microphone through the app's own plugin so the answer
 * is remembered ("denied" = Android will not show the dialog again) and a
 * dialog-less refusal comes back at once. Resolves `null` when this install
 * has no such method (older shell) so the caller can fall back.
 */
export async function requestNativeMicrophonePermission(): Promise<'granted' | 'denied' | 'prompt' | null> {
  if (!isNativeApp() || typeof AudioRoute.requestMicrophonePermission !== 'function') return null
  try {
    const { state } = await AudioRoute.requestMicrophonePermission()
    return state
  } catch {
    return null
  }
}

/** Opens Android/iOS app settings so the user can grant mic/camera permissions. */
export async function openNativeAppSettings(): Promise<boolean> {
  if (!isNativeApp()) return false
  try {
    await AudioRoute.openAppSettings()
    return true
  } catch {
    return false
  }
}

export async function getNativeRouteSnapshot(): Promise<CallAudioRouteSnapshot | null> {
  if (!isNativeApp() || typeof AudioRoute.getRouteSnapshot !== 'function') return null
  try {
    const snapshot = await AudioRoute.getRouteSnapshot()
    if (snapshot && typeof snapshot.kind === 'string') rememberSnapshot(snapshot)
    return snapshot
  } catch {
    return null
  }
}

export async function setNativeOutputDevice(id: string): Promise<boolean> {
  if (!isNativeApp() || typeof AudioRoute.setOutputDevice !== 'function') return false
  try {
    const { ok } = await AudioRoute.setOutputDevice({ id })
    return ok === true
  } catch {
    return false
  }
}

/** iOS: present the system AVRoutePickerView. Android: not available (JS sheet). */
export async function showNativeRoutePicker(): Promise<boolean> {
  if (!isNativeApp() || typeof AudioRoute.showRoutePicker !== 'function') return false
  try {
    const { shown } = await AudioRoute.showRoutePicker()
    return shown === true
  } catch {
    return false
  }
}

export function classifyHardwareKind(raw: string | undefined | null): HardwareAudioRoute {
  const value = (raw ?? '').toLowerCase()
  if (value.includes('car') || value.includes('bus') || value.includes('dock')) return 'car'
  if (
    value.includes('bluetooth') ||
    value.includes('ble') ||
    value.includes('a2dp') ||
    value.includes('sco') ||
    value.includes('hfp')
  ) {
    return 'bluetooth'
  }
  if (
    value.includes('wired') ||
    value.includes('headset') ||
    value.includes('headphone') ||
    value.includes('usb') ||
    value.includes('line')
  ) {
    return 'wired'
  }
  if (value.includes('speaker')) return 'speaker'
  return 'earpiece'
}
